import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { GetAllAddressesAsync, AllAddressesData } from "../../Admin-Api/GetAllAddressesAsync";
import { deleteFeedbackAdmin } from "../../Admin-Api/DeleteAddressAsync";

import { getAllDeletedAddressAsync, DeletedAddress } from "../../Admin-Api/GetAllDeletedAddressAsync";
import EditIcon from "../../assets/icons/edit.svg";
import { Search } from "lucide-react";
import { SearchAddressesAsync } from "../../Admin-Api/SearchAddressesAsync";

interface CustomJwtPayload {
    RoleId: string;
}

const ManageAddress: React.FC = () => {
    const navigate = useNavigate();
    // const [addresses, setAddresses] = useState<AllAddressesData[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(15);
    const totalPages = Math.ceil(totalRecords / pageSize);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const [viewMode, setViewMode] = useState<"active" | "deleted">("active");
    const [addresses, setAddresses] = useState<(AllAddressesData | DeletedAddress)[]>([]);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate("/");
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                if (decodedToken.RoleId !== "1") {
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate("/");
                }
            } catch (error) {
                toast.error("Invalid token. Please log in again.");
                navigate("/");
            }
        }
    }, [navigate]);

    const fetchAddresses = async (
        page: number,
        search: string = searchText
    ) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("No auth token");

            if (viewMode === "active") {

                if (search.trim()) {
                    setSearchLoading(true);
                } else {
                    setLoading(true);
                }

                let res;

                if (search.trim()) {
                    res = await SearchAddressesAsync(
                        search,
                        page,
                        pageSize,
                        token
                    );
                } else {
                    res = await GetAllAddressesAsync(
                        page,
                        pageSize,
                        token
                    );
                }

                if (!res) return;

                setAddresses(res.data);
                setSelectedIds([]);
                setSelectAll(false);
                setTotalRecords(res.totalRecords);
                setPageNumber(page);

            } else {
                setLoading(true);

                const res = await getAllDeletedAddressAsync(token, (key) => key);

                setAddresses(res || []);
                setTotalRecords(res?.length || 0);
                setPageNumber(1);
            }

        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch addresses");
        } finally {
            setLoading(false);
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses(1);
    }, [viewMode]);

    useEffect(() => {
        if (viewMode !== "active") return;

        const timer = setTimeout(() => {
            if (searchText.trim() === "") {
                fetchAddresses(1, "");
                return;
            }

            if (searchText.trim().length < 2) return;

            fetchAddresses(1, searchText.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchText]);

    const handlePageChange = (page: number) => {
        fetchAddresses(page, searchText);
    };

    const handleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const updated = prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id];

            setSelectAll(updated.length === addresses.length);

            return updated;
        });
    };

    const handleSelectAll = () => {
        const currentPageIds = addresses.map((addr) => addr.id);

        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
        } else {
            setSelectedIds(currentPageIds);
            setSelectAll(true);
        }
    };

    const getVisiblePages = () => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // First pages
    if (pageNumber <= 3) {
        return [1, 2, 3, 4, "...", totalPages];
    }

    // Last pages
    if (pageNumber >= totalPages - 2) {
        return [
            1,
            "...",
            totalPages - 3,
            totalPages - 2,
            totalPages - 1,
            totalPages,
        ];
    }

    // Middle
    return [
        1,
        "...",
        pageNumber - 1,
        pageNumber,
        pageNumber + 1,
        "...",
        totalPages,
    ];
};

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);

            const token = localStorage.getItem("token") || "";

            const idsToDelete =
                selectedIds.length > 0
                    ? selectedIds
                    : selectedId
                        ? [selectedId]
                        : [];

            if (idsToDelete.length === 0) return;

            for (const id of idsToDelete) {
                await deleteFeedbackAdmin(id, token);
            }

            toast.success("Deleted successfully");

            setSelectedIds([]);
            setSelectAll(false);

            // Refresh safely
            if (addresses.length === idsToDelete.length && pageNumber > 1) {
                fetchAddresses(pageNumber - 1);
            } else {
                fetchAddresses(pageNumber);
            }

        } catch (err) {
            console.error(err);
            toast.error("Delete failed");
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
            setSelectedId(null);
        }
    };


    return (
        <div className="min-h-screen bg-white flex flex-col">
            <ToastContainer />

            {/* Header */}
            <header className="flex items-center justify-between px-4 sm:px-8 py-4 bg-white shadow-sm">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                    <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                    Manage Addresses
                </h1>
            </header>

            {/* Content */}
            {/* Content */}
            <main className="flex-1 overflow-auto px-6 py-5 bg-[#f5f7fb]">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {/* Toggle and actions */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-b border-gray-200">                            <div className="flex items-center gap-8">

                            <button
                                type="button"
                                onClick={() => {
                                    setViewMode("active");
                                    setSearchText("");
                                    setSelectedIds([]);
                                    setSelectAll(false);
                                }}
                                className={`h-[36px] flex items-center text-sm font-medium border-b-2 transition ${viewMode === "active"
                                    ? "text-blue-600 border-blue-600"
                                    : "text-gray-500 border-transparent hover:text-gray-700"
                                    }`}
                            >
                                Addresses List
                                {viewMode === "active" && (
                                    <span className="ml-1">({totalRecords})</span>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setViewMode("deleted");
                                    setSearchText("");
                                    setSelectedIds([]);
                                    setSelectAll(false);
                                }}
                                className={`h-[36px] flex items-center text-sm font-medium border-b-2 transition ${viewMode === "deleted"
                                    ? "text-blue-600 border-blue-600"
                                    : "text-gray-500 border-transparent hover:text-gray-700"
                                    }`}
                            >
                                Deleted Addresses
                                {viewMode === "deleted" && (
                                    <span className="ml-1">({totalRecords})</span>
                                )}
                            </button>
                        </div>



                            {viewMode === "active" && (
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={selectedIds.length === 0 || loading}
                                        className={`h-10 px-4 rounded-lg text-sm font-medium transition ${selectedIds.length === 0 || loading
                                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                            : "text-red-600 border border-red-600 hover:bg-red-50"
                                            }`}
                                    >
                                        Delete Selected ({selectedIds.length})
                                    </button>

                                    <div className="relative w-[300px]">
                                        <Search
                                            size={17}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                                        />

                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            placeholder="Search by address, PAM code, DigiPin..."
                                            className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />

                                        {searchLoading && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Table */}
                        <div className="px-5 py-4">
                            {loading && !searchLoading ? (
                                <div className="rounded-xl p-8 text-center text-gray-500">
                                    {viewMode === "active"
                                        ? "Loading addresses..."
                                        : "Loading deleted addresses..."}
                                </div>
                            ) : addresses.length === 0 ? (
                                <div className="rounded-xl p-8 text-center text-gray-500">
                                    {viewMode === "active"
                                        ? "No addresses found."
                                        : "No deleted addresses found."}
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-md">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="bg-blue-500 border-b border-gray-200">
                                                {viewMode === "active" && (
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectAll}
                                                            onChange={handleSelectAll}
                                                            aria-label="Select all addresses"
                                                        />
                                                    </th>
                                                )}

                                                {[
                                                    "Address",
                                                    "PAM Code",
                                                    // ...(viewMode === "active" ? ["DigiPin"] : []),
                                                    "Type",
                                                    "User",
                                                    "Created On",
                                                    ...(viewMode === "active" ? ["Action"] : []),
                                                ].map((header) => (
                                                    <th
                                                        key={header}
                                                        className={`px-4 py-3 text-xs font-semibold text-white uppercase tracking-wide ${header === "Action"
                                                            ? "text-center"
                                                            : "text-left"
                                                            }`}
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-200">
                                            {addresses.map((addr) => (
                                                <tr
                                                    key={addr.id}
                                                    className={`transition-colors duration-150 ${selectedIds.includes(addr.id)
                                                        ? "bg-blue-50"
                                                        : "hover:bg-blue-50/50"
                                                        }`}
                                                >
                                                    {viewMode === "active" && (
                                                        <td className="px-4 py-4 text-left">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(addr.id)}
                                                                onChange={() => handleSelect(addr.id)}
                                                                aria-label={`Select address ${addr.id}`}
                                                            />
                                                        </td>
                                                    )}

                                                    <td className="px-4 py-4 text-left">
                                                        <div className="max-w-[280px]">
                                                            <p className="text-[14px] font-medium text-[#202124] truncate">
                                                                {searchText.trim()
                                                                    ? addr.addressLine1 || "Not Provided"
                                                                    : addr.addressType || "Not Provided"}
                                                            </p>

                                                            <p className="mt-1 text-xs text-gray-500 truncate">
                                                                {(searchText.trim()
                                                                    ? [addr.addressLine2, addr.addressLine3]
                                                                    : [addr.addressLine1, addr.addressLine2, addr.addressLine3]
                                                                )
                                                                    .filter(Boolean)
                                                                    .join(", ") || "-"}
                                                            </p>
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-4 text-left text-[14px] text-blue-600 font-medium whitespace-nowrap">
                                                        {addr.pinCode &&
                                                            addr.blockCode &&
                                                            addr.addressCode
                                                            ? `${addr.pinCode}-${addr.blockCode}-${addr.addressCode}`
                                                            : "-"}
                                                    </td>

                                                    {/* {viewMode === "active" && (
                                                        <td className="px-4 py-4 text-left text-[14px] text-[#202124] whitespace-nowrap">
                                                            {"digiPin" in addr
                                                                ? addr.digiPin || "-"
                                                                : "-"}
                                                        </td>
                                                    )} */}

                                                    <td className="px-4 py-4 text-left text-[14px]">
                                                        <span
                                                            className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${addr.isPrivate
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-blue-100 text-blue-700"
                                                                }`}
                                                        >
                                                            {addr.isPrivate ? "Private" : "Public"}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-4 text-left text-[14px] text-[#202124] whitespace-nowrap">
                                                        {addr.userName || "Not Provided"}
                                                    </td>

                                                    <td className="px-4 py-4 text-left text-[14px] text-[#202124] whitespace-nowrap">
                                                        {addr.createdDate
                                                            ? new Date(
                                                                addr.createdDate
                                                            ).toLocaleDateString("en-GB")
                                                            : "-"}
                                                    </td>

                                                    {viewMode === "active" && (
                                                        <td className="px-4 py-4 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(
                                                                    `/edit-address-admin/${addr.id}`,
                                                                    {
                                                                        state: {
                                                                            displayPamCode: addr.pinCode && addr.blockCode && addr.addressCode
                                                                                ? `${addr.pinCode}-${addr.blockCode}-${addr.addressCode}`
                                                                                : "",
                                                                        },
                                                                    }
                                                                )}
                                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-blue-100 transition"
                                                                title="Edit address"
                                                            >
                                                                <img
                                                                    src={EditIcon}
                                                                    alt="Edit"
                                                                    className="w-4 h-4"
                                                                />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {viewMode === "active" &&
                            addresses.length > 0 &&
                            totalPages > 1 && (
                                <div className="flex justify-center mt-6 px-2 pb-5">

                                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-sm px-2 py-1 flex-wrap justify-center">

                                        {/* Previous */}
                                        <button
                                            onClick={() => handlePageChange(pageNumber - 1)}
                                            disabled={pageNumber === 1 || loading}
                                            className="px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                        >
                                            «
                                        </button>

                                        {/* Page Numbers */}
                                        {getVisiblePages().map((item, index) =>
                                            item === "..." ? (
                                                <span key={`dots-${index}`} className="px-2 text-gray-400">
                                                    ...
                                                </span>
                                            ) : (
                                                <button
                                                    key={item}
                                                    onClick={() => handlePageChange(item as number)}
                                                    className={`min-w-[38px] h-[38px] rounded-lg text-sm font-medium transition ${pageNumber === item
                                                            ? "bg-blue-600 text-white shadow-sm"
                                                            : "text-gray-700 hover:bg-gray-100"
                                                        }`}
                                                >
                                                    {item}
                                                </button>
                                            )
                                        )}

                                        {/* Next */}
                                        <button
                                            onClick={() => handlePageChange(pageNumber + 1)}
                                            disabled={pageNumber === totalPages || loading}
                                            className="px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                        >
                                            »
                                        </button>

                                    </div>

                                </div>
                            )}
                    </div>
                </div>
            </main>


            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-[90%] max-w-md">

                        <h2 className="text-lg font-semibold text-gray-800 mb-2">
                            Delete Address
                        </h2>

                        <p className="text-sm text-gray-600 mb-5">
                            Are you sure you want to delete the selected addresses?
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                className="px-4 py-2  text-red-600 border border-red-600 rounded-lg  disabled:opacity-50 flex items-center gap-2"
                            >
                                {isDeleting && (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                )}

                                {isDeleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageAddress;